import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  HandledGraphQLMutationError
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  listPlansQuery,
  getPlanQuery,
  getPlanPricingQuery,
  createPlanMutation,
  updatePlanMutation
} from "../../Graphql/querries/planQuery";
import {
  ListPlansResponse,
  GetPlanResponse,
  GetPlanPricingResponse,
  CreatePlanResponse,
  UpdatePlanResponse,
  ListPlansInput,
  GetPlanInput,
  GetPlanPricingInput,
  CreatePlanInput,
  UpdatePlanInput
} from "../../Graphql/types/plan";
import { setPlanList, setPlanDetail } from "../../store/slices/planSlice";
import { RootState } from "../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export const useGetPlanList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ListPlansResponse, Error, ListPlansInput | void>({
    mutationKey: ["listPlans"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching plan list",
        buildLogContext("membership", "list-plans", userId, companyId, storeId)
      );

      const response: ListPlansResponse = await fetchGraphQL(
        listPlansQuery,
        { input: input ?? {} },
        setAlert
      );

      const listResult = response?.listPlans;

      // ❌ Unexpected response shape
      if (!listResult) {
        captureAppException(
          "List plans failed: missing listPlans",
          new Error("List plans failed: missing listPlans"),
          buildLogContext(
            "membership",
            "list-plans",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "List plans failed: missing listPlans"
        );
      }

      // ⚠️ Expected API/business failure
      if (listResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "List plans rejected by API",
          buildLogContext(
            "membership",
            "list-plans",
            userId,
            companyId,
            storeId,
            { apiCode: String(listResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: listResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `List plans rejected by API: ${listResult.code}`,
          listResult.code
        );
      }

      // ✅ Success
      dispatch(setPlanList(listResult.planList ?? []));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Plan list fetched successfully",
        buildLogContext(
          "membership",
          "list-plans",
          userId,
          companyId,
          storeId,
          { planCount: listResult.planList?.length ?? 0 }
        )
      );

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch plan list",
        error,
        buildLogContext("membership", "list-plans", userId, companyId, storeId)
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useGetPlanDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<GetPlanResponse, Error, GetPlanInput>({
    mutationKey: ["getPlan"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching plan detail",
        buildLogContext("membership", "get-plan", userId, companyId, storeId, {
          planId: String(input.planId)
        })
      );

      const response: GetPlanResponse = await fetchGraphQL(
        getPlanQuery,
        { input },
        setAlert
      );

      const getResult = response?.getPlan;

      // ❌ Unexpected response shape
      if (!getResult) {
        captureAppException(
          "Get plan failed: missing getPlan",
          new Error("Get plan failed: missing getPlan"),
          buildLogContext(
            "membership",
            "get-plan",
            userId,
            companyId,
            storeId,
            { planId: String(input.planId) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Get plan failed: missing getPlan"
        );
      }

      // ⚠️ Expected API/business failure
      if (getResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Get plan detail rejected by API",
          buildLogContext(
            "membership",
            "get-plan",
            userId,
            companyId,
            storeId,
            {
              planId: String(input.planId),
              apiCode: String(getResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: getResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Get plan detail rejected by API: ${getResult.code}`,
          getResult.code
        );
      }

      // ✅ Success
      dispatch(setPlanDetail(getResult.plan ?? null));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Plan detail fetched successfully",
        buildLogContext("membership", "get-plan", userId, companyId, storeId, {
          planId: String(input.planId)
        })
      );

      return response;
    },

    onError: (error, input) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch plan detail",
        error,
        buildLogContext("membership", "get-plan", userId, companyId, storeId, {
          planId: String(input?.planId)
        })
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useGetPlanPricing = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<GetPlanPricingResponse, Error, GetPlanPricingInput>({
    mutationKey: ["getPlanPricing"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching plan pricing",
        buildLogContext(
          "membership",
          "get-plan-pricing",
          userId,
          companyId,
          storeId,
          { planId: String(input.planId) }
        )
      );

      const response: GetPlanPricingResponse = await fetchGraphQL(
        getPlanPricingQuery,
        { input },
        setAlert
      );

      const pricingResult = response?.getPlanPricing;

      // ❌ Unexpected response shape
      if (!pricingResult) {
        captureAppException(
          "Get plan pricing failed: missing getPlanPricing",
          new Error("Get plan pricing failed: missing getPlanPricing"),
          buildLogContext(
            "membership",
            "get-plan-pricing",
            userId,
            companyId,
            storeId,
            { planId: String(input.planId) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Get plan pricing failed: missing getPlanPricing"
        );
      }

      // ⚠️ Expected API/business failure
      if (pricingResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Get plan pricing rejected by API",
          buildLogContext(
            "membership",
            "get-plan-pricing",
            userId,
            companyId,
            storeId,
            {
              planId: String(input.planId),
              apiCode: String(pricingResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: pricingResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Get plan pricing rejected by API: ${pricingResult.code}`,
          pricingResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Plan pricing fetched successfully",
        buildLogContext(
          "membership",
          "get-plan-pricing",
          userId,
          companyId,
          storeId,
          { planId: String(input.planId) }
        )
      );

      return response;
    },

    onError: (error, input) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch plan pricing",
        error,
        buildLogContext(
          "membership",
          "get-plan-pricing",
          userId,
          companyId,
          storeId,
          { planId: String(input?.planId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useCreatePlan = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<CreatePlanResponse, Error, CreatePlanInput>({
    mutationKey: ["createPlan"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating plan",
        buildLogContext(
          "membership",
          "create-plan",
          userId,
          companyId,
          storeId,
          { planName: String(input.name) }
        )
      );

      const response: CreatePlanResponse = await fetchGraphQL(
        createPlanMutation,
        { input },
        setAlert
      );

      const createResult = response?.createPlan;

      // ❌ Unexpected response shape
      if (!createResult) {
        captureAppException(
          "Create plan failed: missing createPlan",
          new Error("Create plan failed: missing createPlan"),
          buildLogContext(
            "membership",
            "create-plan",
            userId,
            companyId,
            storeId,
            { planName: String(input.name) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create plan failed: missing createPlan"
        );
      }

      // ⚠️ Expected API/business failure
      if (createResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Plan creation rejected by API",
          buildLogContext(
            "membership",
            "create-plan",
            userId,
            companyId,
            storeId,
            {
              planName: String(input.name),
              apiCode: String(createResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: createResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Plan creation rejected by API: ${createResult.code}`,
          createResult.code
        );
      }

      // ❌ Logically broken success response
      if (!createResult.plan?.planId) {
        captureAppException(
          "API returned OK but plan data missing after createPlan",
          new Error("API returned OK but plan data missing after createPlan"),
          buildLogContext(
            "membership",
            "create-plan",
            userId,
            companyId,
            storeId,
            { planName: String(input.name) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "API returned OK but plan data missing after createPlan"
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Plan created successfully",
        buildLogContext(
          "membership",
          "create-plan",
          userId,
          companyId,
          storeId,
          { planId: String(createResult.plan.planId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to create plan",
        error,
        buildLogContext("membership", "create-plan", userId, companyId, storeId)
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useUpdatePlan = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    UpdatePlanResponse,
    Error,
    { planId: string; input: UpdatePlanInput }
  >({
    mutationKey: ["updatePlan"],

    mutationFn: async ({ planId, input }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating plan",
        buildLogContext(
          "membership",
          "update-plan",
          userId,
          companyId,
          storeId,
          { planId }
        )
      );

      const response: UpdatePlanResponse = await fetchGraphQL(
        updatePlanMutation,
        { planId, input },
        setAlert
      );

      const updateResult = response?.updatePlan;

      // ❌ Unexpected response shape
      if (!updateResult) {
        captureAppException(
          "Update plan failed: missing updatePlan",
          new Error("Update plan failed: missing updatePlan"),
          buildLogContext(
            "membership",
            "update-plan",
            userId,
            companyId,
            storeId,
            { planId }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Update plan failed: missing updatePlan"
        );
      }

      // ⚠️ Expected API/business failure
      if (updateResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Plan update rejected by API",
          buildLogContext(
            "membership",
            "update-plan",
            userId,
            companyId,
            storeId,
            { planId, apiCode: String(updateResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: updateResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Plan update rejected by API: ${updateResult.code}`,
          updateResult.code
        );
      }

      // ✅ Success — only dispatch to Redux after confirming OK
      if (updateResult.plan) {
        dispatch(setPlanDetail(updateResult.plan));
      }

      sendAppLog(
        LOG_LEVEL.INFO,
        "Plan updated successfully",
        buildLogContext(
          "membership",
          "update-plan",
          userId,
          companyId,
          storeId,
          { planId }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });

      return response;
    },

    onError: (error, { planId }) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to update plan",
        error,
        buildLogContext(
          "membership",
          "update-plan",
          userId,
          companyId,
          storeId,
          { planId: String(planId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};
